/**
 * Settings Page
 *
 * Comprehensive user settings and preferences
 */
import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { authStore } from '@/stores/AuthStore'
import { workspaceStore } from '@/stores/WorkspaceStore'
import {
  UserIcon,
  KeyIcon,
  BellIcon,
  PaintBrushIcon,
  LockClosedIcon,
  BuildingOfficeIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { useTheme, ACCENT_COLOR_STYLES, type AccentColor } from '@/contexts/ThemeContext'
import { NAVIGATION } from '@/components/layout/Sidebar'
import toast from 'react-hot-toast'
import { apiClient } from '@/services/api'
import { useEffect } from 'react'

type SettingsTab = 'profile' | 'appearance' | 'sidebar' | 'api-keys' | 'preferences' | 'security' | 'workspace' | 'pricing' | 'pii-proxy'

const SettingsPage = observer(() => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
  const [isSaving, setIsSaving] = useState(false)

  // Profile state
  const [fullName, setFullName] = useState(authStore.user?.full_name || '')
  const [email, setEmail] = useState(authStore.user?.email || '')

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  // API Keys state
  const [apiKeys, setApiKeys] = useState([
    { id: '1', name: 'Production Key', key: 'sk-prod-xxxx...xxxx', created: '2025-11-01', lastUsed: '2025-11-05' },
    { id: '2', name: 'Development Key', key: 'sk-dev-xxxx...xxxx', created: '2025-10-15', lastUsed: '2025-11-04' },
  ])
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({})
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Pricing state
  const [pricing, setPricing] = useState<Record<string, { input: number; output: number; is_custom: boolean }>>({})
  const [editingPricing, setEditingPricing] = useState<Record<string, { input: string; output: string }>>({})
  const [loadingPricing, setLoadingPricing] = useState(false)

  useEffect(() => {
    if (activeTab === 'pricing') {
      const fetchPricing = async () => {
        setLoadingPricing(true)
        try {
          const resp = await (apiClient as any).client.get('/api/v1/telemetry/pricing')
          setPricing(resp.data?.pricing || {})
        } catch {
          setPricing({})
        } finally {
          setLoadingPricing(false)
        }
      }
      fetchPricing()
    }
  }, [activeTab])

  const handleSavePricing = async (model: string) => {
    const edit = editingPricing[model]
    if (!edit) return
    const inputPrice = parseFloat(edit.input)
    const outputPrice = parseFloat(edit.output)
    if (isNaN(inputPrice) || isNaN(outputPrice) || inputPrice < 0 || outputPrice < 0) {
      toast.error('Invalid price values')
      return
    }
    try {
      await (apiClient as any).client.put(
        `/api/v1/telemetry/pricing/${encodeURIComponent(model)}`,
        null,
        { params: { input_price: inputPrice, output_price: outputPrice } }
      )
      setPricing(prev => ({
        ...prev,
        [model]: { input: inputPrice, output: outputPrice, is_custom: true }
      }))
      setEditingPricing(prev => {
        const next = { ...prev }
        delete next[model]
        return next
      })
      toast.success(`Pricing updated for ${model}`)
    } catch {
      toast.error('Failed to update pricing')
    }
  }

  const handleResetPricing = async (model: string) => {
    try {
      await (apiClient as any).client.delete(`/api/v1/telemetry/pricing/${encodeURIComponent(model)}`)
      // Refetch to get default values
      const resp = await (apiClient as any).client.get('/api/v1/telemetry/pricing')
      setPricing(resp.data?.pricing || {})
      toast.success(`Pricing reset for ${model}`)
    } catch {
      toast.error('Failed to reset pricing')
    }
  }

  const handleResetAllPricing = async () => {
    try {
      await (apiClient as any).client.post('/api/v1/telemetry/pricing/reset')
      const resp = await (apiClient as any).client.get('/api/v1/telemetry/pricing')
      setPricing(resp.data?.pricing || {})
      setEditingPricing({})
      toast.success('All pricing reset to defaults')
    } catch {
      toast.error('Failed to reset pricing')
    }
  }

  // PII Proxy state
  const [piiEnabled, setPiiEnabled] = useState<boolean | null>(null)
  const [piiToggling, setPiiToggling] = useState(false)
  const [piiStatus, setPiiStatus] = useState<any>(null)

  useEffect(() => {
    if (activeTab === 'pii-proxy') {
      const fetchPii = async () => {
        try {
          const [toggleResp, statusResp] = await Promise.all([
            (apiClient as any).client.get('/api/v1/pii-proxy/global-toggle'),
            (apiClient as any).client.get('/api/v1/pii-proxy/status').catch(() => ({ data: null })),
          ])
          setPiiEnabled(toggleResp.data?.enabled ?? false)
          setPiiStatus(statusResp.data)
        } catch {
          setPiiEnabled(false)
          setPiiStatus(null)
        }
      }
      fetchPii()
    }
  }, [activeTab])

  const handleTogglePii = async () => {
    if (piiEnabled === null) return
    setPiiToggling(true)
    try {
      const newState = !piiEnabled
      await (apiClient as any).client.put(`/api/v1/pii-proxy/global-toggle?enabled=${newState}`)
      setPiiEnabled(newState)
      toast.success(`PII Proxy ${newState ? 'enabled' : 'disabled'}`)
      // Refresh status
      try {
        const statusResp = await (apiClient as any).client.get('/api/v1/pii-proxy/status')
        setPiiStatus(statusResp.data)
      } catch {}
    } catch {
      toast.error('Failed to toggle PII Proxy')
    } finally {
      setPiiToggling(false)
    }
  }

  // Notifications state (appearance theme moved to ThemeContext)
  const [notifications, setNotifications] = useState({
    email: true,
    browser: true,
    executionComplete: true,
    errors: true,
    updates: false,
  })

  // Theme + sidebar preferences from context
  const {
    theme: currentTheme,
    setTheme: setCurrentTheme,
    resolvedTheme,
    accentColor,
    setAccentColor,
    accentColors,
    hiddenPages,
    setHiddenPages,
    pageOrder,
    setPageOrder,
    resetSidebar,
  } = useTheme()

  const tabs = [
    { id: 'profile' as SettingsTab, name: 'Profile', icon: UserIcon },
    { id: 'appearance' as SettingsTab, name: 'Appearance', icon: PaintBrushIcon },
    { id: 'sidebar' as SettingsTab, name: 'Sidebar', icon: Squares2X2Icon },
    { id: 'api-keys' as SettingsTab, name: 'API Keys', icon: KeyIcon },
    { id: 'preferences' as SettingsTab, name: 'Notifications', icon: BellIcon },
    { id: 'security' as SettingsTab, name: 'Security', icon: LockClosedIcon },
    { id: 'workspace' as SettingsTab, name: 'Workspace', icon: BuildingOfficeIcon },
    { id: 'pricing' as SettingsTab, name: 'Token Pricing', icon: CurrencyDollarIcon },
    { id: 'pii-proxy' as SettingsTab, name: 'PII Proxy', icon: ShieldCheckIcon },
  ]

  // Sidebar settings helpers
  const togglePageVisibility = (path: string) => {
    if (hiddenPages.includes(path)) {
      setHiddenPages(hiddenPages.filter((p) => p !== path))
    } else {
      setHiddenPages([...hiddenPages, path])
    }
  }
  const movePageUp = (path: string) => {
    const current = pageOrder.length > 0
      ? [...pageOrder]
      : NAVIGATION.map((n) => n.to)
    const idx = current.indexOf(path)
    if (idx <= 0) return
    ;[current[idx - 1], current[idx]] = [current[idx], current[idx - 1]]
    setPageOrder(current)
  }
  const movePageDown = (path: string) => {
    const current = pageOrder.length > 0
      ? [...pageOrder]
      : NAVIGATION.map((n) => n.to)
    const idx = current.indexOf(path)
    if (idx === -1 || idx >= current.length - 1) return
    ;[current[idx + 1], current[idx]] = [current[idx], current[idx + 1]]
    setPageOrder(current)
  }
  // Order the pages for display: use pageOrder if set, else default NAVIGATION order
  const orderedPages = (() => {
    if (pageOrder.length === 0) return NAVIGATION
    return [...NAVIGATION].sort((a, b) => {
      const ai = pageOrder.indexOf(a.to)
      const bi = pageOrder.indexOf(b.to)
      if (ai === -1 && bi === -1) return 0
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
  })()

  const handleUpdateProfile = async () => {
    setIsSaving(true)
    try {
      // API call to update profile
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulated
      toast.success('Profile updated successfully')
    } catch (error) {
      toast.error('Failed to update profile')
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields')
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }

    setIsSaving(true)
    try {
      // API call to change password
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulated
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error) {
      toast.error('Failed to change password')
    } finally {
      setIsSaving(false)
    }
  }

  const handleGenerateApiKey = async () => {
    const keyName = prompt('Enter a name for this API key:')
    if (!keyName) return

    setIsSaving(true)
    try {
      // API call to generate new key
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulated
      const newKey = {
        id: String(apiKeys.length + 1),
        name: keyName,
        key: `sk-${Math.random().toString(36).substr(2, 32)}`,
        created: new Date().toISOString().split('T')[0],
        lastUsed: 'Never',
      }
      setApiKeys([...apiKeys, newKey])
      toast.success('API key generated successfully')
    } catch (error) {
      toast.error('Failed to generate API key')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRevokeApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return
    }

    setIsSaving(true)
    try {
      // API call to revoke key
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulated
      setApiKeys(apiKeys.filter(k => k.id !== keyId))
      toast.success('API key revoked successfully')
    } catch (error) {
      toast.error('Failed to revoke API key')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key)
    setCopiedKey(key)
    toast.success('API key copied to clipboard')
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const handleSavePreferences = async () => {
    setIsSaving(true)
    try {
      // API call to save preferences
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulated
      toast.success('Preferences saved successfully')
    } catch (error) {
      toast.error('Failed to save preferences')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account, preferences, and API keys</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }
              `}
            >
              <tab.icon className="w-5 h-5" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Profile Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Role
                  </label>
                  <Badge variant={authStore.isAdmin ? 'error' : 'primary'}>
                    {authStore.user?.role.toUpperCase()}
                  </Badge>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleUpdateProfile}
                    disabled={isSaving}
                    variant="primary"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <div className="max-w-4xl space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">API Keys</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage your API keys for programmatic access
                  </p>
                </div>
                <Button onClick={handleGenerateApiKey} disabled={isSaving}>
                  Generate New Key
                </Button>
              </div>

              <div className="space-y-3">
                {apiKeys.map((apiKey) => (
                  <div
                    key={apiKey.id}
                    className="p-4 border border-border rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <KeyIcon className="w-5 h-5 text-muted-foreground" />
                          <span className="font-medium text-foreground">{apiKey.name}</span>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-sm bg-muted/50 px-3 py-1 rounded font-mono">
                            {showKeys[apiKey.id] ? apiKey.key : '••••••••••••••••'}
                          </code>
                          <button
                            onClick={() => setShowKeys({ ...showKeys, [apiKey.id]: !showKeys[apiKey.id] })}
                            className="text-muted-foreground hover:text-muted-foreground"
                          >
                            {showKeys[apiKey.id] ? (
                              <EyeSlashIcon className="w-5 h-5" />
                            ) : (
                              <EyeIcon className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleCopyKey(apiKey.key)}
                            className="text-muted-foreground hover:text-muted-foreground"
                          >
                            {copiedKey === apiKey.key ? (
                              <CheckIcon className="w-5 h-5 text-green-600" />
                            ) : (
                              <ClipboardDocumentIcon className="w-5 h-5" />
                            )}
                          </button>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Created: {apiKey.created} • Last used: {apiKey.lastUsed}
                        </div>
                      </div>

                      <Button
                        onClick={() => handleRevokeApiKey(apiKey.id)}
                        variant="secondary"
                        disabled={isSaving}
                        className="ml-4"
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}

                {apiKeys.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <KeyIcon className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                    <p>No API keys yet</p>
                    <p className="text-sm">Generate your first API key to get started</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Appearance Tab — theme mode + accent color + preview */}
        {activeTab === 'appearance' && (
          <div className="max-w-3xl space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-1">Theme</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select your preferred color mode
              </p>
              <div className="grid grid-cols-3 gap-3">
                {([
                  { id: 'light' as const, label: 'Light', hint: 'Bright and clean', Icon: SunIcon, bg: 'bg-amber-100 text-amber-600' },
                  { id: 'dark' as const, label: 'Dark', hint: 'Easy on the eyes', Icon: MoonIcon, bg: 'bg-slate-800 text-slate-200' },
                  { id: 'system' as const, label: 'System', hint: 'Match device', Icon: ComputerDesktopIcon, bg: 'bg-gradient-to-br from-amber-100 to-slate-800 text-foreground' },
                ]).map(({ id, label, hint, Icon, bg }) => {
                  const selected = currentTheme === id
                  return (
                    <button
                      key={id}
                      onClick={() => setCurrentTheme(id)}
                      className={`flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                        selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${bg}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="text-center">
                        <span className="font-medium text-foreground block">{label}</span>
                        <span className="text-xs text-muted-foreground">{hint}</span>
                      </div>
                      {selected && <CheckIcon className="w-5 h-5 text-primary" />}
                    </button>
                  )
                })}
              </div>
              {currentTheme === 'system' && (
                <p className="mt-4 text-sm text-muted-foreground bg-muted rounded-lg p-3">
                  Currently using <strong>{resolvedTheme}</strong> mode based on your system preference
                </p>
              )}
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-1">Accent Color</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose your primary highlight color
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {accentColors.map((color) => {
                  const style = ACCENT_COLOR_STYLES[color as AccentColor]
                  const selected = accentColor === color
                  return (
                    <button
                      key={color}
                      onClick={() => setAccentColor(color)}
                      className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                        selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full ${style.bg}`} />
                      <span className="text-xs font-medium text-foreground">{style.label}</span>
                    </button>
                  )
                })}
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-1">Preview</h3>
              <p className="text-sm text-muted-foreground mb-4">See how your theme looks</p>
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium">
                    Primary Button
                  </button>
                  <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg font-medium">
                    Secondary
                  </button>
                  <button className="px-4 py-2 border border-border text-foreground rounded-lg font-medium hover:bg-muted">
                    Outline
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm font-medium">
                    Primary Badge
                  </span>
                  <span className="px-2 py-1 bg-destructive/10 text-destructive rounded text-sm font-medium">
                    Destructive
                  </span>
                  <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-sm font-medium">
                    Muted
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-primary" />
                  <div className="h-2 flex-1 bg-primary/20 rounded-full">
                    <div className="h-full w-2/3 bg-primary rounded-full" />
                  </div>
                  <span className="text-sm text-muted-foreground">66%</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Sidebar Tab — visibility toggles + reorder */}
        {activeTab === 'sidebar' && (
          <div className="max-w-3xl space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-foreground">Sidebar Navigation</h3>
                {(hiddenPages.length > 0 || pageOrder.length > 0) && (
                  <button
                    onClick={resetSidebar}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5" />
                    Reset
                  </button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Toggle visibility and reorder pages in your sidebar
              </p>

              <div className="space-y-1">
                {orderedPages.map((page, idx) => {
                  const isVisible = !hiddenPages.includes(page.to)
                  const canMoveUp = idx > 0
                  const canMoveDown = idx < orderedPages.length - 1
                  const Icon = page.icon
                  return (
                    <div
                      key={page.to}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
                        isVisible ? 'hover:bg-muted/50' : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex flex-col">
                          <button
                            onClick={() => movePageUp(page.to)}
                            disabled={!canMoveUp}
                            className="h-4 w-4 inline-flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:hover:text-muted-foreground"
                            title="Move up"
                          >
                            <ChevronUpIcon className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => movePageDown(page.to)}
                            disabled={!canMoveDown}
                            className="h-4 w-4 inline-flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:hover:text-muted-foreground"
                            title="Move down"
                          >
                            <ChevronDownIcon className="w-3 h-3" />
                          </button>
                        </div>
                        {isVisible ? (
                          <EyeIcon className="w-4 h-4 text-primary shrink-0" />
                        ) : (
                          <EyeSlashIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                        )}
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span
                          className={`text-sm font-medium truncate ${
                            isVisible ? 'text-foreground' : 'text-muted-foreground'
                          }`}
                        >
                          {page.name}
                        </span>
                      </div>
                      <button
                        onClick={() => togglePageVisibility(page.to)}
                        className="shrink-0"
                        title={isVisible ? 'Hide from sidebar' : 'Show in sidebar'}
                      >
                        <div
                          className={`w-9 h-5 rounded-full transition-colors relative ${
                            isVisible ? 'bg-primary' : 'bg-border'
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                              isVisible ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </div>
                      </button>
                    </div>
                  )
                })}
              </div>
            </Card>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <p className="text-sm text-primary">
                Hidden pages are only removed from the sidebar — you can still access them via their direct URL (e.g. <code className="bg-muted px-1 rounded">/settings</code>).
              </p>
            </div>
          </div>
        )}

        {/* Notifications Tab (was "Preferences") */}
        {activeTab === 'preferences' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                <BellIcon className="w-5 h-5 inline mr-2" />
                Notifications
              </h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={notifications.email}
                    onChange={(e) => setNotifications({ ...notifications, email: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Email notifications</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={notifications.browser}
                    onChange={(e) => setNotifications({ ...notifications, browser: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Browser notifications</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={notifications.executionComplete}
                    onChange={(e) => setNotifications({ ...notifications, executionComplete: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Notify when executions complete</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={notifications.errors}
                    onChange={(e) => setNotifications({ ...notifications, errors: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Notify on errors</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={notifications.updates}
                    onChange={(e) => setNotifications({ ...notifications, updates: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Product updates and announcements</span>
                </label>
              </div>

              <div className="pt-4 mt-4 border-t border-border">
                <Button
                  onClick={handleSavePreferences}
                  disabled={isSaving}
                  variant="primary"
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Change Password</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                    >
                      {showCurrentPassword ? (
                        <EyeSlashIcon className="w-5 h-5" />
                      ) : (
                        <EyeIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="w-5 h-5" />
                      ) : (
                        <EyeIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleChangePassword}
                    disabled={isSaving}
                    variant="primary"
                  >
                    {isSaving ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Two-Factor Authentication</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add an extra layer of security to your account
              </p>
              <Button variant="secondary" disabled>
                Enable 2FA (Coming Soon)
              </Button>
            </Card>
          </div>
        )}

        {/* Workspace Tab */}
        {activeTab === 'workspace' && (
          <div className="max-w-2xl space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Workspace Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Workspace Name</p>
                  <p className="text-sm font-medium text-foreground">
                    {workspaceStore.currentWorkspace?.name || 'Default Workspace'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm font-medium text-foreground">
                    {workspaceStore.currentWorkspace?.description || 'No description'}
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Danger Zone</h3>
              <div className="space-y-3">
                <div className="p-4 border border-red-500/20 rounded-lg bg-red-500/10">
                  <h4 className="text-sm font-semibold text-red-600 mb-2">Delete Account</h4>
                  <p className="text-sm text-red-600 mb-3">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button variant="secondary" disabled>
                    Delete Account
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Token Pricing Configuration</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Prices per 1M tokens (USD). Custom values override defaults for cost calculations.
                  </p>
                </div>
                <button
                  onClick={handleResetAllPricing}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors"
                >
                  Reset All to Defaults
                </button>
              </div>

              {loadingPricing ? (
                <p className="text-sm text-muted-foreground py-4">Loading pricing...</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground font-medium">Model</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Input ($/1M)</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Output ($/1M)</th>
                        <th className="text-center py-2 text-muted-foreground font-medium">Status</th>
                        <th className="text-right py-2 text-muted-foreground font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(pricing).sort(([a], [b]) => a.localeCompare(b)).map(([model, p]) => {
                        const isEditing = model in editingPricing
                        return (
                          <tr key={model} className="border-b border-border/50 hover:bg-muted/20">
                            <td className="py-2 font-mono text-foreground text-xs">{model}</td>
                            <td className="py-2 text-right">
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingPricing[model].input}
                                  onChange={(e) => setEditingPricing(prev => ({
                                    ...prev,
                                    [model]: { ...prev[model], input: e.target.value }
                                  }))}
                                  className="w-24 px-2 py-1 text-right bg-background border border-border rounded text-xs"
                                />
                              ) : (
                                <span className="text-foreground">${p.input.toFixed(3)}</span>
                              )}
                            </td>
                            <td className="py-2 text-right">
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingPricing[model].output}
                                  onChange={(e) => setEditingPricing(prev => ({
                                    ...prev,
                                    [model]: { ...prev[model], output: e.target.value }
                                  }))}
                                  className="w-24 px-2 py-1 text-right bg-background border border-border rounded text-xs"
                                />
                              ) : (
                                <span className="text-foreground">${p.output.toFixed(3)}</span>
                              )}
                            </td>
                            <td className="py-2 text-center">
                              {p.is_custom ? (
                                <Badge variant="warning">Custom</Badge>
                              ) : (
                                <Badge variant="gray">Default</Badge>
                              )}
                            </td>
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-1">
                                {isEditing ? (
                                  <>
                                    <button
                                      onClick={() => handleSavePricing(model)}
                                      className="px-2 py-1 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingPricing(prev => {
                                        const next = { ...prev }
                                        delete next[model]
                                        return next
                                      })}
                                      className="px-2 py-1 text-xs rounded bg-muted text-foreground hover:bg-border"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setEditingPricing(prev => ({
                                        ...prev,
                                        [model]: { input: p.input.toString(), output: p.output.toString() }
                                      }))}
                                      className="px-2 py-1 text-xs rounded bg-muted text-foreground hover:bg-border"
                                    >
                                      Edit
                                    </button>
                                    {p.is_custom && (
                                      <button
                                        onClick={() => handleResetPricing(model)}
                                        className="px-2 py-1 text-xs rounded bg-red-500/10 text-red-600 hover:bg-red-500/20"
                                      >
                                        Reset
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
        {activeTab === 'pii-proxy' && (
          <div className="space-y-6">
            {/* Global Toggle */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">PII Proxy</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Automatically detects and tokenises personally identifiable information (PII) before it reaches the LLM.
                    Enable globally, then configure per-project in the Operations &gt; PII Proxy tab.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${piiEnabled ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {piiEnabled === null ? 'Loading...' : piiEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    onClick={handleTogglePii}
                    disabled={piiToggling || piiEnabled === null}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      piiEnabled ? 'bg-primary' : 'bg-muted'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        piiEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </Card>

            {/* Status */}
            {piiStatus && piiEnabled && (
              <Card>
                <h3 className="text-lg font-semibold text-foreground mb-4">Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="px-3 py-2 bg-muted/30 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground">Registered Projects</p>
                    <p className="text-xl font-bold text-foreground">{piiStatus.registered_projects || 0}</p>
                  </div>
                  <div className="px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <p className="text-xs text-muted-foreground">Active Projects</p>
                    <p className="text-xl font-bold text-emerald-600">{piiStatus.active_projects || 0}</p>
                  </div>
                  <div className="px-3 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <p className="text-xs text-muted-foreground">Shadow Mode</p>
                    <p className="text-xl font-bold text-amber-600">{piiStatus.shadow_projects || 0}</p>
                  </div>
                  <div className="px-3 py-2 bg-muted/30 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground">Pattern Version</p>
                    <p className="text-xl font-bold text-foreground">{piiStatus.pattern_version || 'N/A'}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* How it works */}
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-3">How it works</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">1.</span>
                  <p><strong className="text-foreground">Intercept</strong> — Middleware catches all agent/crew invocation requests</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">2.</span>
                  <p><strong className="text-foreground">Scan</strong> — Regex + NER detect PII (emails, phone numbers, NHS numbers, etc.)</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">3.</span>
                  <p><strong className="text-foreground">Tokenise</strong> — PII replaced with safe tokens: <code className="px-1 bg-muted rounded text-xs">{"<<EMAIL_1>>"}</code></p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">4.</span>
                  <p><strong className="text-foreground">Execute</strong> — Agent sees only tokens, never raw PII</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-emerald-500 font-bold mt-0.5">5.</span>
                  <p><strong className="text-foreground">Restore</strong> — Response de-tokenised before returning to the caller</p>
                </div>
              </div>
            </Card>

            {/* Quick actions */}
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-3">Configuration</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Per-project configuration, custom term registry, consent management, and scan testing
                are available in the <strong>Operations &gt; PII Proxy</strong> tab.
              </p>
              <button
                onClick={() => window.location.href = '/operations'}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Go to PII Proxy Configuration
              </button>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
})

export default SettingsPage
